import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AccessibilityState {
  keyboardNavigation: boolean;
  screenReader: boolean;
  highlightLinks: boolean;
  highlightTitles: boolean;
  fontSize: number;
  lineSpacing: number;
  readableFont: boolean;
  readingAid: boolean;
  readingMode: boolean;
  blockAnimations: boolean;
  readingMask: boolean;
  reverseContrast: boolean;
  brightContrast: boolean;
  colorlessDisplay: boolean;
  darkContrast: boolean;
  highSaturation: boolean;
  textColor: string;
  cursorColor: string;
  bigCursor: boolean;
  backgroundColor: string;
  titleColor: string;
  showImageDescriptions: boolean;
  setKeyboardNavigation: (value: boolean) => void;
  setScreenReader: (value: boolean) => void;
  setHighlightLinks: (value: boolean) => void;
  setHighlightTitles: (value: boolean) => void;
  setFontSize: (value: number) => void;
  setLineSpacing: (value: number) => void;
  setReadableFont: (value: boolean) => void;
  setReadingAid: (value: boolean) => void;
  setReadingMode: (value: boolean) => void;
  setBlockAnimations: (value: boolean) => void;
  setReadingMask: (value: boolean) => void;
  setReverseContrast: (value: boolean) => void;
  setBrightContrast: (value: boolean) => void;
  setColorlessDisplay: (value: boolean) => void;
  setDarkContrast: (value: boolean) => void;
  setHighSaturation: (value: boolean) => void;
  setTextColor: (value: string) => void;
  setCursorColor: (value: string) => void;
  setBigCursor: (value: boolean) => void;
  setBackgroundColor: (value: string) => void;
  setTitleColor: (value: string) => void;
  setShowImageDescriptions: (value: boolean) => void;
  resetSettings: () => void;
}

const initialState = {
  keyboardNavigation: false,
  screenReader: false,
  highlightLinks: false,
  highlightTitles: false,
  fontSize: 16,
  lineSpacing: 1.5,
  readableFont: false,
  readingAid: false,
  readingMode: false,
  blockAnimations: false,
  readingMask: false,
  reverseContrast: false,
  brightContrast: false,
  colorlessDisplay: false,
  darkContrast: false,
  highSaturation: false,
  textColor: 'black',
  cursorColor: 'black',
  bigCursor: false,
  backgroundColor: 'white',
  titleColor: 'black',
  showImageDescriptions: false
};

export const useAccessibilityStore = create<AccessibilityState>()(
  persist(
    (set) => ({
      ...initialState,
      setKeyboardNavigation: (value) => set({ keyboardNavigation: value }),
      setScreenReader: (value) => set({ screenReader: value }),
      setHighlightLinks: (value) => set({ highlightLinks: value }),
      setHighlightTitles: (value) => set({ highlightTitles: value }),
      setFontSize: (value) => set({ fontSize: value }),
      setLineSpacing: (value) => set({ lineSpacing: value }),
      setReadableFont: (value) => set({ readableFont: value }),
      setReadingAid: (value) => set({ readingAid: value }),
      setReadingMode: (value) => set({ readingMode: value }),
      setBlockAnimations: (value) => set({ blockAnimations: value }),
      setReadingMask: (value) => set({ readingMask: value }),
      setReverseContrast: (value) => set({ reverseContrast: value }),
      setBrightContrast: (value) => set({ brightContrast: value }),
      setColorlessDisplay: (value) => set({ colorlessDisplay: value }),
      setDarkContrast: (value) => set({ darkContrast: value }),
      setHighSaturation: (value) => set({ highSaturation: value }),
      setTextColor: (value) => set({ textColor: value }),
      setCursorColor: (value) => set({ cursorColor: value }),
      setBigCursor: (value) => set({ bigCursor: value }),
      setBackgroundColor: (value) => set({ backgroundColor: value }),
      setTitleColor: (value) => set({ titleColor: value }),
      setShowImageDescriptions: (value) => set({ showImageDescriptions: value }),
      resetSettings: () => set(initialState)
    }),
    {
      name: 'accessibility-settings'
    }
  )
);